import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import { ExecutionRequestStatus } from "@/lib/interfaces and Enums/ENUMExecutionRequestStatus";
import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import { StrategyType } from "@/lib/interfaces and Enums/ENUMStrategyType";
import { NotationToDecimalValueType } from "@/lib/interfaces and Enums/TYPENotations";
import { ITradeSettingsStore } from "@/store/tradeSettingsStore";
import tradeSettingsStore from "@/store/tradeSettingsStore";
import tradeDataStore from "@/store/tradeDataStore";
import { ITradeSettings } from "@/store/tradeSettingsStoreACTIONS";

/**
 * Determines the reference price value from trade settings
 * Maps the orderPriceValue from the Select Panel input to the leg table
 *
 * @param esid - The execution strategy ID
 * @param tradeSettings - All trade settings from the store
 * @returns The reference price value or null if not applicable
 */
export function determineReferencePriceFromSettings(
  esid: string,
  tradeSettings: ITradeSettings[],
): number | string | null {
  const thisTradeSettings = tradeSettings.find((trade) => trade.esid === esid);

  if (!thisTradeSettings) {
    return null;
  }

  const settings = thisTradeSettings.settings;

  // Check if this is an Order trade type
  const isOrderType =
    settings.tradeTypes.find((type) => type.active)?.name === "Order";

  if (!isOrderType) {
    return null;
  }

  // Check if "Trade at" is set to "Price" (for Outright trades)
  const tradeAtPrice =
    settings.orderSetLevelOutright.find((type) => type.active)?.name ===
    "Price";

  if (tradeAtPrice) {
    return settings.orderPriceValue || null;
  }

  return null;
}

/**
 * Checks if the reference price cell should show a value
 * based on the trade type and settings
 */
export function shouldShowReferencePrice(
  settings: ITradeSettingsStore | undefined,
  currentStatus: ExecutionStrategyStatus | ExecutionRequestStatus,
  isOutright: boolean,
): boolean {
  if (!settings) {
    return false;
  }
  if (currentStatus != ExecutionStrategyStatus.New) {
    return false;
  }

  const isOrderType =
    settings.tradeTypes.find((type) => type.active)?.name === "Order";

  if (!isOrderType) {
    return false;
  }

  const tradeAtPrice =
    settings.orderSetLevelOutright.find((type) => type.active)?.name === "Leg";

  const setLevelPrice =
    settings.orderSetLevelStructure.find((type) => type.active)?.name ===
    "Spread / Legs";

  if (isOutright) {
    return tradeAtPrice;
  }
  return setLevelPrice;
}

/**
 * AG Grid editable callback for the reference price cell.
 * Uses the same logic as shouldShowReferencePrice but accesses stores directly.
 *
 * @param params - AG Grid cell params
 * @returns boolean indicating if the cell should be editable
 */
export function isReferencePriceCellEditable(params: any): boolean {
  if (!params.data) {
    return false;
  }

  const { fullTradeData } = returnAllTradeDetailsFromGenericParams(params);
  const esid = fullTradeData?.esid;

  if (!esid) {
    return false;
  }

  // Access stores directly (not via React hooks)
  const currentTradeData = tradeDataStore.getState().tradeData;
  const thisTrade = currentTradeData.find((trade) => trade.esid === esid);

  if (!thisTrade) {
    return false;
  }

  const currentStatus = thisTrade.status;
  const isOutright = thisTrade.strategyType === StrategyType.Outright;

  if (!currentStatus) {
    return false;
  }

  const tradeSettings =
    tradeSettingsStore.getState().tradeSettingsData;
  const thisTradeSettings = tradeSettings.find((trade) => trade.esid === esid);

  return shouldShowReferencePrice(
    thisTradeSettings?.settings,
    currentStatus,
    isOutright,
  );
}

/**
 * Gets the notation for a leg based on blockId
 * Checks both store data and fullTradeData from params
 *
 * @param esid - The execution strategy ID
 * @param blockId - The block ID to find notation for
 * @param fullTradeData - Optional fullTradeData from params (available from start)
 * @returns The notation string (e.g., "bps", "%") or undefined
 */
export function getNotationForLeg(
  esid: string,
  blockId: number | string | undefined,
  fullTradeData?: any,
): NotationToDecimalValueType | undefined {
  // 1. Try fullTradeData first (available from props immediately)
  if (fullTradeData?.legs && fullTradeData.legs.length > 0) {
    if (blockId) {
      const matchingLeg = fullTradeData.legs.find(
        (leg: any) => leg.blockId == blockId,
      );
      if (matchingLeg?.notation) {
        return matchingLeg.notation as NotationToDecimalValueType;
      }
    }
    // Fall back to first leg notation
    if (fullTradeData.legs[0]?.notation) {
      return fullTradeData.legs[0].notation as NotationToDecimalValueType;
    }
  }

  // 2. Fall back to store data
  if (!esid) {
    return undefined;
  }

  const currentTradeData = tradeDataStore.getState().tradeData;
  const thisTrade = currentTradeData.find((trade) => trade.esid === esid);

  if (!thisTrade?.legs) {
    return undefined;
  }

  if (blockId) {
    const matchingLeg = thisTrade.legs.find((leg) => leg.blockId == blockId);
    if (matchingLeg?.notation) {
      return matchingLeg.notation as NotationToDecimalValueType;
    }
  }

  // Fall back to first leg notation
  return thisTrade.legs[0]?.notation as NotationToDecimalValueType | undefined;
}
